import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { trackResiliencePreviewCompleted } from "@/lib/analytics";

const questions = [
  { q: "How do plan and fulfilment talk to each other?", a: ["Spreadsheets and email", "Weekly export", "Nightly sync", "One shared system"] },
  { q: "When demand changes, how fast does the build plan change?", a: ["Next quarter", "Next month", "Next week", "Same day"] },
  { q: "Who owns an order exception end to end?", a: ["Nobody in particular", "Whoever notices", "A named team", "A named person with a dashboard"] },
];

const bandOf = (score: number) => (score <= 3 ? "Fragile" : score <= 6 ? "Developing" : "Resilient");

const detailOf = (band: string) =>
  band === "Fragile"
    ? "The handoffs are the product problem. Start with the two-week diagnostic."
    : band === "Developing"
      ? "Parts connect, ownership does not. The full score will show where."
      : "Strong operating spine. The full score checks demand, product and AI readiness.";

const ResiliencePreview = () => {
  const reduced = useReducedMotion();
  const [answers, setAnswers] = useState<Array<number | undefined>>([undefined, undefined, undefined]);
  const answered = answers.filter((x) => x !== undefined).length;
  const complete = answered === questions.length;
  const score = answers.reduce<number>((s, x) => s + (x ?? 0), 0);
  const band = bandOf(score);
  const tracked = useRef<string | null>(null);

  useEffect(() => {
    if (!complete) {
      tracked.current = null;
      return;
    }
    if (tracked.current === band) return;
    tracked.current = band;
    trackResiliencePreviewCompleted(band);
  }, [complete, band]);

  const pct = complete ? (score / 9) * 100 : (answered / questions.length) * 0;

  return (
    <section id="score" className="section-tint py-16 md:py-24">
      <div className="container mx-auto">
        <div className="grid gap-10 min-[900px]:grid-cols-2 min-[900px]:gap-14">
          <div>
            <p className="eyebrow">Try the diagnostic</p>
            <h2 className="mt-4">Where does your operation stand? Three questions, thirty seconds.</h2>
            <p className="lede mt-5">A provisional read, computed here, no email needed. The full eight-question Resilience Score and a senior-reviewed scorecard are one click further.</p>

            {/* live result — stays in view while the questions are answered */}
            <div className="surface-card mt-7 p-5 md:p-6" aria-live="polite">
              <div className="flex items-center gap-5">
                <div
                  className="grid h-[84px] w-[84px] flex-none place-items-center rounded-full transition-[background] duration-500"
                  style={{ background: `conic-gradient(#1FA3A3 ${pct}%, #DCE7EF 0)` }}
                  aria-hidden
                >
                  <span className="grid h-[64px] w-[64px] place-items-center rounded-full bg-background font-mono text-sm text-ink">
                    {complete ? `${score}/9` : `${answered}/3`}
                  </span>
                </div>
                <div>
                  <p className="font-mono text-[.66rem] text-steel-2">{complete ? "PROVISIONAL BAND" : "YOUR ANSWERS"}</p>
                  {complete ? (
                    <motion.div
                      key={band}
                      initial={reduced ? false : { opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.45, ease: [0.2, 0.7, 0.2, 1] }}
                    >
                      <p className="mt-1 text-2xl font-semibold text-ink">
                        {band} <span className="text-base font-normal text-ink-3">· {score} of 9</span>
                      </p>
                      <p className="mt-2 text-sm leading-relaxed text-ink-2">{detailOf(band)}</p>
                    </motion.div>
                  ) : (
                    <p className="mt-1 text-sm leading-relaxed text-ink-2">
                      Answer {questions.length - answered} more {questions.length - answered === 1 ? "question" : "questions"} to see your provisional band.
                    </p>
                  )}
                </div>
              </div>

              {complete && (
                <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-line pt-5">
                  <Button asChild>
                    <Link to="/resources/resilience-score">Take the full Resilience Score <ArrowRight /></Link>
                  </Button>
                  <button
                    type="button"
                    onClick={() => setAnswers([undefined, undefined, undefined])}
                    className="text-sm font-medium text-steel-2 underline-offset-4 hover:underline"
                  >
                    Start again
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="surface-card p-5 md:p-7">
            <p className="font-mono text-[.66rem] text-steel-2">{answered} OF {questions.length} ANSWERED</p>
            <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-line" aria-hidden>
              <div className="h-full bg-grad-progress transition-[width] duration-500" style={{ width: `${(answered / questions.length) * 100}%` }} />
            </div>

            {questions.map((item, i) => (
              <fieldset key={item.q} className="mt-6">
                <legend className="mb-3 text-sm font-semibold text-ink">{item.q}</legend>
                <div className="flex flex-wrap gap-2">
                  {item.a.map((a, v) => (
                    <label
                      key={a}
                      className={`relative cursor-pointer rounded-md border px-3 py-2 text-xs transition-colors duration-200 ${
                        answers[i] === v ? "border-steel-2 bg-steel-2 text-primary-foreground" : "border-line-2 bg-background text-ink-2 hover:border-steel-2"
                      }`}
                    >
                      <input
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        type="radio"
                        name={`q${i}`}
                        value={v}
                        checked={answers[i] === v}
                        onChange={() => setAnswers((old) => old.map((x, j) => (j === i ? v : x)))}
                      />
                      <span className="relative">{a}</span>
                      <span className="sr-only"> — {v} points</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            ))}

            {!complete && (
              <p className="mt-6 border-t border-line pt-5 text-sm text-ink-3">
                Each answer is 0 to 3 points, no weighting, no black box. Your provisional band appears as soon as all three are answered.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResiliencePreview;
